import * as React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  AssessmentOutlined,
  CleaningServicesOutlined,
  ContentCopyOutlined,
  DownloadOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import useTranslation from "components/customHooks/translations";
import { useLoadingContext } from "components/contexts/LoadingContext";
import { useSnackbarContext } from "components/contexts/SnackbarContext";
import { errorMessage } from "helpers";
import { IUser } from "interfaces";
import { getUsersNoPagination } from "services/users";
import {
  cleanupCsvReports,
  generateCsvReports,
  getCsvReportDownloadUrl,
  listCsvReports,
  ReportFile,
} from "services/reports";

type ReportScope = "all_partners" | "partner";

interface ReportRow {
  id: string;
  key: string;
  filename: string;
  size: number;
  sizeLabel: string;
  lastModified: string;
}

interface GeneratedReportLink {
  label: string;
  filename: string;
  url: string;
  s3Key: string;
}

const Reports = () => {
  const translation = useTranslation();
  const reportText = translation.reports;
  const theme = useTheme();
  const { setLoading } = useLoadingContext();
  const { setErrorMessage, setSuccessMessage } = useSnackbarContext();

  const [scope, setScope] = React.useState<ReportScope>("all_partners");
  const [partners, setPartners] = React.useState<IUser[]>([]);
  const [selectedPartner, setSelectedPartner] = React.useState<IUser | null>(
    null
  );
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [daysOld, setDaysOld] = React.useState(7);
  const [rows, setRows] = React.useState<ReportRow[]>([]);
  const [generatedReports, setGeneratedReports] = React.useState<
    GeneratedReportLink[]
  >([]);
  const [generatedSummary, setGeneratedSummary] = React.useState<{
    successful?: number;
    failed?: number;
    totalPartners?: number;
    appsCount?: number;
    emailsCount?: number;
    period?: string;
  } | null>(null);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isListing, setIsListing] = React.useState(false);
  const [isCleaning, setIsCleaning] = React.useState(false);
  const [confirmCleanupOpen, setConfirmCleanupOpen] = React.useState(false);

  const selectedPartnerLabel = selectedPartner
    ? formatPartnerLabel(selectedPartner)
    : "";
  const periodLabel =
    startDate && endDate ? `${startDate} to ${endDate}` : reportText.allDates;

  const dateRangeError = Boolean(startDate || endDate) && !(startDate && endDate);
  const partnerError = scope === "partner" && !selectedPartner?.id;

  const loadReports = React.useCallback(() => {
    setIsListing(true);
    setLoading(true);

    return listCsvReports()
      .then((res) => {
        const files = Array.isArray(res.data.files) ? res.data.files : [];
        setRows(filesToRows(files));
      })
      .catch((e) => {
        setErrorMessage(errorMessage(e));
      })
      .finally(() => {
        setIsListing(false);
        setLoading(false);
      });
  }, [setErrorMessage, setLoading]);

  React.useEffect(() => {
    setIsListing(true);
    setLoading(true);

    Promise.all([getUsersNoPagination(), listCsvReports()])
      .then(([usersResponse, reportsResponse]) => {
        const users = usersResponse.data["hydra:member"] || [];
        setPartners(users.filter(isPartnerUser));

        const files = Array.isArray(reportsResponse.data.files)
          ? reportsResponse.data.files
          : [];
        setRows(filesToRows(files));
      })
      .catch((e) => {
        setErrorMessage(errorMessage(e));
      })
      .finally(() => {
        setIsListing(false);
        setLoading(false);
      });
  }, [setErrorMessage, setLoading]);

  const handleScopeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextScope = event.target.value as ReportScope;
    setScope(nextScope);
    setGeneratedReports([]);
    setGeneratedSummary(null);

    if (nextScope === "all_partners") {
      setSelectedPartner(null);
    }
  };

  const handleGenerateReports = () => {
    if (partnerError) {
      setErrorMessage(reportText.choosePartnerError);
      return;
    }

    if (dateRangeError) {
      setErrorMessage(reportText.chooseDateRangeError);
      return;
    }

    if (startDate && endDate && startDate > endDate) {
      setErrorMessage(reportText.invalidDateOrderError);
      return;
    }

    setIsGenerating(true);
    setLoading(true);

    generateCsvReports({
      type: scope,
      partner_id: scope === "partner" ? selectedPartner?.id : undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    })
      .then((res) => {
        const reports = getGeneratedReportLinks(res.data, reportText);
        setGeneratedReports(reports);
        setGeneratedSummary({
          successful: res.data.successful_reports,
          failed: res.data.failed_reports,
          totalPartners: res.data.total_partners,
          appsCount: res.data.apps_count,
          emailsCount: res.data.emails_count,
          period:
            res.data.report_period?.start_date && res.data.report_period?.end_date
              ? `${res.data.report_period.start_date} to ${res.data.report_period.end_date}`
              : undefined,
        });
        setSuccessMessage(
          scope === "partner"
            ? reportText.partnerGenerateSuccess
            : reportText.allGenerateSuccess
        );
        return loadReports();
      })
      .catch((e) => {
        setErrorMessage(errorMessage(e));
      })
      .finally(() => {
        setIsGenerating(false);
        setLoading(false);
      });
  };

  const handleDownload = (key: string) => {
    const filename = key.replace(/^csv-reports\//, "");
    setLoading(true);

    getCsvReportDownloadUrl(filename)
      .then((res) => {
        window.open(res.data.download_url, "_blank", "noopener,noreferrer");
      })
      .catch((e) => {
        setErrorMessage(errorMessage(e));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCopy = (value: string, successMessage: string) => {
    if (!navigator.clipboard) {
      setErrorMessage(reportText.clipboardUnavailable);
      return;
    }

    navigator.clipboard
      .writeText(value)
      .then(() => setSuccessMessage(successMessage))
      .catch(() => setErrorMessage(reportText.clipboardError));
  };

  const handleCleanup = () => {
    if (!daysOld || daysOld < 1 || daysOld > 365) {
      setErrorMessage(reportText.cleanupRangeError);
      return;
    }

    setIsCleaning(true);
    setLoading(true);

    cleanupCsvReports(daysOld)
      .then((res) => {
        setSuccessMessage(
          `${reportText.cleanupComplete} Deleted ${
            res.data.deleted_s3_files || 0
          } files.`
        );
        setConfirmCleanupOpen(false);
        loadReports();
      })
      .catch((e) => {
        setErrorMessage(errorMessage(e));
      })
      .finally(() => {
        setIsCleaning(false);
        setLoading(false);
      });
  };

  const columns: GridColDef[] = [
    {
      field: "filename",
      headerName: reportText.fileColumn,
      flex: 2,
      minWidth: 280,
      renderCell: (params) => (
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap title={params.row.filename}>
            {params.row.filename}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {params.row.key}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "sizeLabel",
      headerName: reportText.sizeColumn,
      minWidth: 110,
      flex: 0.5,
    },
    {
      field: "lastModified",
      headerName: reportText.lastModifiedColumn,
      minWidth: 180,
      flex: 0.9,
    },
    {
      field: "actions",
      headerName: reportText.actionsColumn,
      minWidth: 150,
      sortable: false,
      filterable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
          <Tooltip title={reportText.downloadReport}>
            <IconButton
              aria-label={`Download ${params.row.filename}`}
              onClick={() => handleDownload(params.row.key)}
              color="primary"
              size="small"
            >
              <DownloadOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={reportText.copyS3Key}>
            <IconButton
              aria-label={`Copy S3 key for ${params.row.filename}`}
              onClick={() =>
                handleCopy(params.row.key, reportText.copySuccess)
              }
              size="small"
            >
              <ContentCopyOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={4}>
      <Stack spacing={1}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h4">{reportText.pageTitle}</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 760, mt: 1 }}
            >
              {reportText.pageDescription}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Button
              variant="outlined"
              startIcon={<RefreshOutlined />}
              onClick={loadReports}
              disabled={isListing}
            >
              {reportText.refreshList}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <AssessmentOutlined color="primary" />
          <Box>
            <Typography variant="h5">{reportText.generateTitle}</Typography>
            <Typography variant="body2" color="text.secondary">
              {reportText.generateDescription}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={3}>
          <FormControl>
            <FormLabel>{reportText.reportScope}</FormLabel>
            <RadioGroup
              row
              value={scope}
              onChange={handleScopeChange}
              aria-label="Report scope"
            >
              <FormControlLabel
                value="all_partners"
                control={<Radio />}
                label={reportText.allPartners}
              />
              <FormControlLabel
                value="partner"
                control={<Radio />}
                label={reportText.singlePartner}
              />
            </RadioGroup>
            <FormHelperText>
              {reportText.scopeHelper}
            </FormHelperText>
          </FormControl>

          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", lg: "flex-start" }}
          >
            <Autocomplete
              disabled={scope === "all_partners"}
              options={partners}
              value={selectedPartner}
              onChange={(_, value) => setSelectedPartner(value)}
              getOptionLabel={(partner) => formatPartnerLabel(partner)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ minWidth: { xs: "100%", lg: 360 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={reportText.partner}
                  error={partnerError}
                  helperText={
                    partnerError
                      ? reportText.partnerRequired
                      : scope === "all_partners"
                      ? reportText.partnerDisabled
                      : reportText.partnerHelper
                  }
                />
              )}
            />

            <TextField
              label={reportText.startDate}
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              error={dateRangeError}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: "100%", sm: 180 } }}
            />
            <TextField
              label={reportText.endDate}
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              error={dateRangeError}
              helperText={
                dateRangeError
                  ? reportText.dateRangeRequired
                  : periodLabel
              }
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: "100%", sm: 180 } }}
            />
            <LoadingButton
              variant="contained"
              loading={isGenerating}
              onClick={handleGenerateReports}
              sx={{ minHeight: 56, px: 3 }}
            >
              {reportText.generateButton}
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>

      {generatedSummary && (
        <Alert
          severity={generatedSummary.failed ? "warning" : "success"}
          sx={{ alignItems: "flex-start" }}
        >
          <Stack spacing={1.5}>
            <Typography variant="body2">
              {scope === "partner"
                ? `${reportText.generatedFor} ${selectedPartnerLabel}.`
                : `${reportText.processedPartners}: ${
                    generatedSummary.totalPartners || 0
                  }.`}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {typeof generatedSummary.appsCount === "number" && (
                <Chip
                  label={`${generatedSummary.appsCount} ${reportText.apps}`}
                  size="small"
                />
              )}
              {typeof generatedSummary.emailsCount === "number" && (
                <Chip
                  label={`${generatedSummary.emailsCount} ${reportText.emailAddresses}`}
                  size="small"
                />
              )}
              {typeof generatedSummary.successful === "number" && (
                <Chip
                  label={`${generatedSummary.successful} ${reportText.successful}`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
              {Boolean(generatedSummary.failed) && (
                <Chip
                  label={`${generatedSummary.failed} ${reportText.failed}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
              {generatedSummary.period && (
                <Chip label={generatedSummary.period} size="small" />
              )}
            </Stack>
            {generatedReports.length > 0 && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {generatedReports.map((report) => (
                  <Button
                    key={report.s3Key}
                    href={report.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadOutlined />}
                  >
                    {report.label}
                  </Button>
                ))}
              </Stack>
            )}
          </Stack>
        </Alert>
      )}

      <Divider />

      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "flex-start" }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5">{reportText.storedTitle}</Typography>
            <Typography variant="body2" color="text.secondary">
              {reportText.storedDescription}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              label={reportText.cleanupOlderThan}
              type="number"
              value={daysOld}
              onChange={(event) => setDaysOld(Number(event.target.value))}
              inputProps={{ min: 1, max: 365 }}
              sx={{ width: 190 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">{reportText.days}</InputAdornment>
                ),
              }}
            />
            <LoadingButton
              variant="outlined"
              color="warning"
              loading={isCleaning}
              startIcon={<CleaningServicesOutlined />}
              onClick={() => setConfirmCleanupOpen(true)}
              sx={{ minHeight: 56 }}
            >
              {reportText.cleanupButton}
            </LoadingButton>
          </Stack>
        </Stack>

        <Box sx={{ width: "100%" }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            loading={isListing}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            sx={{
              borderColor: theme.palette.divider,
              ".MuiDataGrid-cell:focus, .MuiDataGrid-columnHeader:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible":
                {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: -2,
                },
            }}
            slots={{
              noRowsOverlay: () => (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: "100%", p: 3 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {reportText.noReports}
                  </Typography>
                </Stack>
              ),
            }}
          />
        </Box>
      </Box>

      <Dialog
        open={confirmCleanupOpen}
        onClose={() => setConfirmCleanupOpen(false)}
        aria-labelledby="cleanup-reports-title"
      >
        <DialogTitle id="cleanup-reports-title">
          {reportText.cleanupDialogTitle}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {reportText.cleanupDialogDescription}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCleanupOpen(false)}>
            {reportText.keepReports}
          </Button>
          <LoadingButton
            color="warning"
            variant="contained"
            loading={isCleaning}
            onClick={handleCleanup}
          >
            {reportText.deleteOldReports}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const formatPartnerLabel = (partner: IUser) => {
  const company = partner.companyInfo?.name;
  const name = [partner.firstName, partner.lastName].filter(Boolean).join(" ");
  const labelParts = [company, name, partner.email].filter(Boolean);

  return Array.from(new Set(labelParts)).join(" - ");
};

const isPartnerUser = (user: IUser) => {
  return Boolean(user.roles?.includes("ROLE_PARTNER"));
};

const fileToRow = (file: ReportFile): ReportRow => {
  const filename = file.key.replace(/^csv-reports\//, "");

  return {
    id: file.key,
    key: file.key,
    filename,
    size: file.size,
    sizeLabel: formatFileSize(file.size),
    lastModified: formatLastModified(file.last_modified),
  };
};

const filesToRows = (files: ReportFile[]) => {
  return files
    .filter(isVisibleReportFile)
    .sort(
      (first, second) =>
        getLastModifiedTime(second.last_modified) -
        getLastModifiedTime(first.last_modified)
    )
    .map(fileToRow);
};

const isVisibleReportFile = (file: ReportFile) => {
  const filename = file.key.replace(/^csv-reports\//, "");
  return !/_(apps|emails|domain_stats)\.csv$/i.test(filename);
};

const getGeneratedReportLinks = (
  data: any,
  reportText: Record<string, string>
): GeneratedReportLink[] => {
  if (data.report) {
    return [
      {
        label: reportText.reportCsv,
        filename: data.report.filename,
        url: data.report.presigned_download_url,
        s3Key: data.report.s3_key,
      },
    ];
  }

  if (Array.isArray(data.report_files)) {
    return data.report_files
      .filter((report: any) => report?.presigned_download_url && report?.s3_key)
      .map((report: any) => ({
        label: reportText.reportCsv,
        filename: report.filename,
        url: report.presigned_download_url,
        s3Key: report.s3_key,
      }));
  }

  return [
    data.apps_report && {
      label: reportText.appsCsv,
      filename: data.apps_report.filename,
      url: data.apps_report.presigned_download_url,
      s3Key: data.apps_report.s3_key,
    },
    data.emails_report && {
      label: reportText.emailsCsv,
      filename: data.emails_report.filename,
      url: data.emails_report.presigned_download_url,
      s3Key: data.emails_report.s3_key,
    },
    data.domain_stats_report && {
      label: reportText.domainStatsCsv,
      filename: data.domain_stats_report.filename,
      url: data.domain_stats_report.presigned_download_url,
      s3Key: data.domain_stats_report.s3_key,
    },
  ].filter(Boolean) as GeneratedReportLink[];
};

const formatFileSize = (bytes: number) => {
  if (!Number.isFinite(bytes)) {
    return "-";
  }

  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  }

  return `${bytes} B`;
};

const formatLastModified = (
  value: string | { date?: string; timezone?: string }
) => {
  const rawValue = typeof value === "string" ? value : value?.date;

  if (!rawValue) {
    return "-";
  }

  const date = new Date(rawValue);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("ro-RO");
};

const getLastModifiedTime = (
  value: string | { date?: string; timezone?: string }
) => {
  const rawValue = typeof value === "string" ? value : value?.date;

  if (!rawValue) {
    return 0;
  }

  const date = new Date(rawValue);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

export default Reports;
